export const isValidZipcode = (zipcode?: string): boolean => {
    const zipCodePattern = /^\d{5}$/;
    return zipcode !== undefined && zipCodePattern.test(zipcode);
};

export const ZIP_CODE_ERROR = 'Invalid ZIP Code.';

export type ZipResponse = {
    reason?: string;
    zipcodes?: {
        county_name?: string;
        state_abbreviation?: string;
        state?: string;
        alternate_counties?: {
            county_name: string;
            state_abbreviation: string;
            state: string;
        }[];
    }[];
};

export type CountyOption = {
    county_name: string;
    state_abbreviation: string;
    state: string;
};

/**
 * Look up county options by zipcode using the BCBSNC BIRPS API
 * Returns primary county and NC alternate counties
 */
export async function getCountyOptionsByZipcode(zipcode: string): Promise<CountyOption[]> {
    if (!isValidZipcode(zipcode)) {
        return [];
    }

    try {
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const ibmClientId = import.meta.env.VITE_DEFAULT_X_IBM_CLIENT_ID || '';

        if (!apiUrl) {
            console.warn('API_URL not configured');
            return [];
        }

        const endpoint = `${apiUrl}/members/v1/api/under65/individualrates?zip=${zipcode}`;
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(ibmClientId ? { 'X-IBM-Client-ID': ibmClientId } : {}),
            },
        });

        if (!response.ok) {
            console.error(`Zipcode lookup failed: ${response.statusText}`);
            return [];
        }

        const data = (await response.json()) as ZipResponse[];


        const counties: CountyOption[] = [];

        if (data && data.length > 0) {
            const primaryZip = data[0].zipcodes?.[0];

            if (primaryZip?.county_name && primaryZip?.state_abbreviation) {
                counties.push({
                    county_name: primaryZip.county_name,
                    state_abbreviation: primaryZip.state_abbreviation,
                    state: primaryZip.state || '',
                });
            }

            // Add NC alternate counties
            const alternates = primaryZip?.alternate_counties || [];

            const ncAlternates = alternates.filter((county) => county.state_abbreviation?.toLowerCase() === 'nc');
            ncAlternates.forEach((county) => {
                // Avoid duplicates
                if (!counties.find((c) => c.county_name === county.county_name)) {
                    counties.push(county);
                }
            });
        }

        return counties;
    } catch (error) {
        console.error('Error looking up zipcode:', error);
        return [];
    }
}

/**
 * Look up county by zipcode using the BCBSNC BIRPS API
 * Same approach as medicare-rate-quote
 */
export async function getCountyByZipcode(zipcode: string): Promise<string | null> {
    if (!isValidZipcode(zipcode)) {
        return null;
    }

    try {
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const ibmClientId = import.meta.env.VITE_DEFAULT_X_IBM_CLIENT_ID || '';

        if (!apiUrl) {
            console.warn('API_URL not configured');
            return null;
        }

        const endpoint = `${apiUrl}/members/v1/api/under65/individualrates?zip=${zipcode}`;
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(ibmClientId ? { 'X-IBM-Client-ID': ibmClientId } : {}),
            },
        });

        if (!response.ok) {
            console.error(`Zipcode lookup failed: ${response.statusText}`);
            return null;
        }

        const data = (await response.json()) as ZipResponse[];


        if (data && data.length > 0) {
            const primaryZip = data[0].zipcodes?.[0];

            if (primaryZip?.county_name) {
                return primaryZip.county_name;
            }

            // Check alternate counties for NC
            const alternates = primaryZip?.alternate_counties || [];

            const ncAlternate = alternates.find((county) => county.state_abbreviation?.toLowerCase() === 'nc');
            if (ncAlternate?.county_name) {
                return ncAlternate.county_name;
            }
        }

        return null;
    } catch (error) {
        console.error('Error looking up zipcode:', error);
        return null;
    }
}
